import { type RefCallback, useCallback, useRef } from 'react';

export type UseSyncElementWidthCssVariableParams<Element extends HTMLElement> = {
  /** CSS custom property on `documentElement`, including the leading `--`. */
  cssVariable: string;
  /**
   * Value written when the node unmounts or `enabled` is false.
   * Omit to remove the property instead.
   */
  clearedValue?: string;
  /** When false, write the cleared value and do not observe. */
  enabled?: boolean;
  /** Extra callback ref invoked with the same node (layout measurement, etc.). */
  forwardRef?: RefCallback<Element> | null;
  /** Border-box width in pixels → the CSS value to publish. */
  getCssValue: (widthPx: number) => string;
};

const writeCssVariable = (cssVariable: string, value: string | undefined) => {
  if (value === undefined) {
    document.documentElement.style.removeProperty(cssVariable);
    return;
  }

  document.documentElement.style.setProperty(cssVariable, value);
};

/**
 * Publishes an element's border-box width to a CSS variable on `<html>`.
 * The upload overlay is a viewport portal, so it cannot inherit in-flow widths
 * from the main rail or the docked agent slot.
 */
export const useSyncElementWidthCssVariable = <Element extends HTMLElement = HTMLElement>({
  clearedValue,
  cssVariable,
  enabled = true,
  forwardRef,
  getCssValue,
}: UseSyncElementWidthCssVariableParams<Element>) => {
  const observerRef = useRef<ResizeObserver | null>(null);
  // Inline `getCssValue` would otherwise reconnect the observer every render.
  const getCssValueRef = useRef(getCssValue);
  getCssValueRef.current = getCssValue;

  const syncElementWidthRef = useCallback<RefCallback<Element>>(
    (node) => {
      forwardRef?.(node);
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!enabled || !node) {
        writeCssVariable(cssVariable, clearedValue);
        return;
      }

      const syncWidth = () => {
        document.documentElement.style.setProperty(
          cssVariable,
          getCssValueRef.current(node.getBoundingClientRect().width),
        );
      };

      syncWidth();

      const observer = new ResizeObserver(syncWidth);
      observer.observe(node);
      observerRef.current = observer;
    },
    [clearedValue, cssVariable, enabled, forwardRef],
  );

  return { syncElementWidthRef };
};
